import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { cn } from '../../utils/cn';

// --- Context & Hook ---

interface LoadingContextType {
    isLoading: boolean;
    startLoading: () => void;
    stopLoading: () => void;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export function LoadingProvider({ children }: { children: ReactNode }) {
    const [loadingCount, setLoadingCount] = useState(0);

    const startLoading = useCallback(() => {
        setLoadingCount(prev => prev + 1);
    }, []);

    const stopLoading = useCallback(() => {
        setLoadingCount(prev => Math.max(0, prev - 1));
    }, []);

    const value = {
        isLoading: loadingCount > 0,
        startLoading,
        stopLoading
    };

    return (
        <LoadingContext.Provider value={value}>
            {children}
        </LoadingContext.Provider>
    );
}

export function useLoading() {
    const context = useContext(LoadingContext);
    if (context === undefined) {
        throw new Error('useLoading must be used within a LoadingProvider');
    }
    return context;
}

// --- Visual Components ---

/**
 * Global Top Progress Bar
 * Responsive to the global loading state managed by LoadingProvider
 */
export function TopLoadingBar() {
    const { isLoading } = useLoading();
    const [progress, setProgress] = useState(0);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (isLoading) {
            setIsVisible(true);
            setProgress(0);

            // Start progress animation
            // Initial jump
            const timer1 = setTimeout(() => setProgress(35), 100);
            // Slow crawl
            const timer2 = setTimeout(() => setProgress(65), 1500);
            const timer3 = setTimeout(() => setProgress(85), 4000);
            const timer4 = setTimeout(() => setProgress(94), 8000);

            return () => {
                [timer1, timer2, timer3, timer4].forEach(clearTimeout);
            };
        } else {
            // Complete transition
            setProgress(100);

            // Hide after completion
            const hideTimer = setTimeout(() => {
                setIsVisible(false);
                setProgress(0);
            }, 600); // Slightly longer for the fade out to look smoother

            return () => clearTimeout(hideTimer);
        }
    }, [isLoading]);

    if (!isVisible) return null;

    return (
        <div className="fixed top-0 left-0 right-0 z-[100001] pointer-events-none h-[3px] overflow-hidden">
            {/* Custom keyframes for the shine and pulse effect */}
            <style>
                {`
                    @keyframes loading-shine {
                        0% { transform: translateX(-100%) skewX(-20deg); }
                        100% { transform: translateX(200%) skewX(-20deg); }
                    }
                    @keyframes loading-pulse {
                        0%, 100% { opacity: 0.6; transform: scaleY(1); }
                        50% { opacity: 1; transform: scaleY(1.2); }
                    }
                `}
            </style>

            <div
                className={cn(
                    "h-full transition-all ease-[cubic-bezier(0.25,0.1,0.25,1)] relative overflow-hidden",
                    isLoading ? "duration-[800ms]" : "duration-[400ms]"
                )}
                style={{
                    width: `${progress}%`,
                    opacity: progress === 100 ? 0 : 1,
                    background: 'linear-gradient(90deg, #4682B4 0%, #60A5FA 50%, #4682B4 100%)',
                    boxShadow: '0 0 15px rgba(96, 165, 250, 0.7), 0 0 5px rgba(70, 130, 180, 0.5)',
                    borderBottomRightRadius: '4px',
                    borderTopRightRadius: '4px',
                    animation: isLoading ? 'loading-pulse 2s infinite ease-in-out' : 'none',
                }}
            >
                {/* Shine Animation Layer */}
                {isLoading && (
                    <div
                        className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/30 to-transparent"
                        style={{
                            animation: 'loading-shine 1.8s infinite linear',
                        }}
                    />
                )}
            </div>
        </div>
    );
}

/**
 * Route level loading trigger
 * Use as a Suspense fallback to automatically trigger the top loading bar
 */
export const RouteLoading = () => {
    const { startLoading, stopLoading } = useLoading();

    useEffect(() => {
        startLoading();
        return () => stopLoading();
    }, [startLoading, stopLoading]);

    // Invisible placeholder to satisfy Suspense without UI flicker
    return <div className="min-h-[200px] w-full invisible" />;
};
